import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowUpRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { hero, SITE, stats } from '../data/mock';
import HeroMusic from './HeroMusic';
import { useCopy } from '../context/SiteCopyContext';

const HeroVideo = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const heroSubtitle = useCopy('home.hero.subtitle', 'Solid timber. Fine fabrics. Furniture built quietly in Australia — designed to sit in your home for decades, not seasons.');
  const ctaPrimary = useCopy('home.hero.cta_primary', 'Shop the collection');
  const ctaSecondary = useCopy('home.hero.cta_secondary', 'Our story');

  // Pause when out of view (perf)
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const toggle = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <section className="relative w-full h-[100vh] min-h-[640px] max-h-[1000px] overflow-hidden bg-[#1E140B]">
      <video
        ref={videoRef}
        src={hero.video}
        poster={hero.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onLoadedData={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
      {!loaded && (
        <img src={hero.fallback} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1E140B]/30 via-transparent to-[#1E140B]/75" />

      {/* Top corner label */}
      <div className="absolute top-7 left-6 lg:left-12 text-[#F5EDDD]/85 hidden md:flex items-center gap-3 z-10">
        <span className="h-px w-10 bg-[#F5EDDD]/70 inline-block draw-line" />
        <span className="text-[11px] tracking-[0.3em] uppercase">Edition n°01 — Bouclé &amp; Timber</span>
      </div>

      {/* Right vertical labels — ladder spread top→bottom */}
      <div className="absolute right-5 lg:right-10 inset-y-0 hidden lg:block z-10 pointer-events-none">
        {[
          { t: 'Solid timber', y: '14%' },
          { t: 'Heavyweight bouclé', y: '36%' },
          { t: 'Made in Melbourne', y: '60%' },
          { t: 'Edition n°01 — 2026', y: '82%' },
        ].map((l, i) => (
          <span
            key={i}
            style={{ top: l.y, writingMode: 'vertical-rl' }}
            className="absolute right-0 text-[10px] tracking-[0.5em] uppercase text-[#F5EDDD]/55 whitespace-nowrap"
          >
            {l.t}
          </span>
        ))}
      </div>

      {/* Content */}
      <div className="relative h-full max-w-[1440px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-32 lg:pb-40 text-[#F5EDDD] z-10">
        <div className="max-w-3xl">
          <h1
            className="text-5xl sm:text-6xl md:text-7xl lg:text-[104px] leading-[0.95] tracking-tight animate-[fadeUp_1100ms_ease-out_both]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span className="block">Where <span className="italic text-[#E8DBC4]">timber</span></span>
            <span className="block">meets <span className="italic text-[#E8DBC4]">thread</span>.</span>
          </h1>
          <p className="mt-7 max-w-xl text-[15px] sm:text-[16px] leading-[1.8] text-[#F5EDDD]/85 animate-[fadeUp_1100ms_ease-out_220ms_both]">
            {heroSubtitle}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4 animate-[fadeUp_1100ms_ease-out_420ms_both]">
            <Button asChild className="bg-[#F5EDDD] text-[#33200F] hover:bg-[#FBF6EC] rounded-none h-12 px-9 text-[12px] tracking-[0.22em] uppercase">
              <Link to="/collections">{ctaPrimary}</Link>
            </Button>
            <Button asChild variant="outline" className="bg-transparent border border-[#F5EDDD]/70 text-[#F5EDDD] hover:bg-[#F5EDDD] hover:text-[#33200F] rounded-none h-12 px-7 text-[12px] tracking-[0.22em] uppercase">
              <Link to="/about">{ctaSecondary} <ArrowUpRight className="ml-2 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-10 hidden lg:flex items-end gap-10 max-w-2xl text-[#F5EDDD]/85 animate-[fadeUp_1100ms_ease-out_580ms_both]">
          {stats.map((m, i) => (
            <div key={i} className="border-l border-[#F5EDDD]/30 pl-3">
              <p className="text-xl text-[#F5EDDD] leading-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{m.k}</p>
              <p className="text-[10px] tracking-[0.2em] uppercase mt-2">{m.v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Video controls — play/pause only (video is silent ambient) */}
      <div className="absolute bottom-7 right-6 lg:right-10 flex items-center gap-2 z-20 text-[#F5EDDD]">
        <button onClick={toggle} aria-label="Play/pause" className="p-3 border border-[#F5EDDD]/40 rounded-full hover:bg-[#F5EDDD] hover:text-[#33200F] transition-colors">
          {playing ? <Pause className="h-4 w-4" strokeWidth={1.5} /> : <Play className="h-4 w-4" strokeWidth={1.5} />}
        </button>
      </div>

      {/* Ambient music control (renders if /media/hero-music.mp3 exists) */}
      <HeroMusic />

      {/* Scroll cue */}
      <div className="absolute bottom-9 left-6 lg:left-12 text-[#F5EDDD]/60 hidden md:flex flex-col items-center gap-2 z-10 animate-[fadeIn_1500ms_ease-out_900ms_both]">
        <span className="text-[10px] tracking-[0.32em] uppercase">Scroll</span>
        <span className="h-10 w-px bg-[#F5EDDD]/40" />
      </div>
    </section>
  );
};

export default HeroVideo;
